import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface Product {
  _id: string;
  name?: string;
  price: number;
}

interface CartItem {
  product: Product;
  quantity: number;
}

interface SuccessState {
  cartItems: CartItem[];
  total: number;
  tipAmount?: number;
}

interface CheckoutSuccessPageProps {
  onCheckoutSuccess?: { type: "GoTo"; route: string };
}

export function CheckoutSuccessPage({ onCheckoutSuccess }: CheckoutSuccessPageProps) {
  const { state } = useLocation<SuccessState | null>();
  const navigate = useNavigate();

  useEffect(() => {
    if (onCheckoutSuccess?.type === "GoTo" && onCheckoutSuccess.route) {
      navigate(onCheckoutSuccess.route, { replace: true });
    }
  }, [onCheckoutSuccess, navigate]);
  const cartItems = state?.cartItems ?? [];
  const total = state?.total ?? 0;
  const tipAmount = state?.tipAmount ?? 0;
  const displayTotal = total + tipAmount;

  return (
    <div className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4">
      <h1 className="text-2xl font-bold mb-4">Thank you for your order!</h1>
      <p className="text-gray-600 mb-6">
        Your payment was successful. Here&apos;s a summary of your order.
      </p>

      {cartItems.length > 0 ? (
        <div className="space-y-4 flex-1">
          <div className="space-y-2">
            {cartItems.map((item) => (
              <div
                key={item.product._id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium">
                    {item.product.name || `Product ${item.product._id}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    ${item.product.price?.toFixed(2) || "0.00"} × {item.quantity}
                  </div>
                </div>
                <div className="font-medium">
                  ${((item.product.price || 0) * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
          {tipAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span>Tip</span>
              <span>${tipAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold pt-2 border-t border-gray-300">
            <span>Total</span>
            <span>${displayTotal.toFixed(2)}</span>
          </div>
        </div>
      ) : (
        <p className="text-gray-600">Your order has been confirmed.</p>
      )}
    </div>
  );
}
